import { create } from 'zustand';
import type {
  ApiClient,
  CreateFolderInput,
  CreatePromptInput,
  UpdateFolderInput,
  UpdatePromptInput,
} from '../../../lib/api';
import { useTeamStore } from './teams';
import type {
  CloudFolder,
  CloudPrompt,
  CloudPromptVersion,
  CreateCloudFolderInput,
  CreateCloudPromptInput,
  RenameCloudFolderInput,
  UpdateCloudPromptInput,
} from '../types/cloud';

export interface CloudPromptsState {
  prompts: CloudPrompt[];
  folders: CloudFolder[];
  isLoading: boolean;
  error: string | null;
  selectedPromptId: string | null;
  searchFilter: string;
}

export interface CloudPromptsActions {
  setApiClient: (client: ApiClient | null) => void;
  fetchPrompts: () => Promise<void>;
  fetchFolders: () => Promise<void>;
  refreshForWorkspace: () => Promise<void>;
  createPrompt: (input: CreateCloudPromptInput) => Promise<CloudPrompt | null>;
  updatePrompt: (id: string, input: UpdateCloudPromptInput) => Promise<CloudPrompt | null>;
  getPromptVersions: (promptId: string) => Promise<CloudPromptVersion[] | null>;
  restorePromptVersion: (promptId: string, versionId: string) => Promise<CloudPrompt | null>;
  refreshPrompt: (prompt: CloudPrompt) => void;
  deletePrompt: (id: string) => Promise<boolean>;
  createFolder: (input: CreateCloudFolderInput) => Promise<CloudFolder | null>;
  renameFolder: (id: string, input: RenameCloudFolderInput) => Promise<CloudFolder | null>;
  deleteFolder: (id: string) => Promise<boolean>;
  setSelectedPromptId: (id: string | null) => void;
  setSearchFilter: (filter: string) => void;
  clearError: () => void;
  reset: () => void;
}

export type CloudPromptsStore = CloudPromptsState & CloudPromptsActions;

const initialState: CloudPromptsState = {
  prompts: [],
  folders: [],
  isLoading: false,
  error: null,
  selectedPromptId: null,
  searchFilter: '',
};

let apiClient: ApiClient | null = null;

export function getApiClient(): ApiClient | null {
  return apiClient;
}

export const useCloudPromptsStore = create<CloudPromptsStore>((set, get) => ({
  ...initialState,

  setApiClient: (client) => {
    apiClient = client;
  },

  fetchPrompts: async () => {
    if (!apiClient) {
      set({ error: 'API client not configured' });
      return;
    }

    set({ isLoading: true, error: null });

    const activeWorkspace = useTeamStore.getState().activeWorkspace;
    const result = activeWorkspace.type === 'team'
      ? await apiClient.getTeamPrompts(activeWorkspace.teamId)
      : await apiClient.getPrompts();

    if (result.success) {
      set({ prompts: result.data.prompts, isLoading: false });
      return;
    }

    set({ error: result.error.message ?? result.error.error, isLoading: false });
  },

  fetchFolders: async () => {
    if (!apiClient) {
      set({ error: 'API client not configured' });
      return;
    }

    set({ isLoading: true, error: null });

    const activeWorkspace = useTeamStore.getState().activeWorkspace;
    const result = activeWorkspace.type === 'team'
      ? await apiClient.getTeamFolders(activeWorkspace.teamId)
      : await apiClient.getFolders();

    if (result.success) {
      set({ folders: result.data.folders, isLoading: false });
      return;
    }

    set({ error: result.error.message ?? result.error.error, isLoading: false });
  },

  refreshForWorkspace: async () => {
    set({
      prompts: [],
      folders: [],
      selectedPromptId: null,
      isLoading: true,
      error: null,
    });

    await get().fetchPrompts();
    await get().fetchFolders();
  },

  createPrompt: async (input) => {
    if (!apiClient) {
      set({ error: 'API client not configured' });
      return null;
    }

    set({ error: null });

    const activeWorkspace = useTeamStore.getState().activeWorkspace;
    const result = activeWorkspace.type === 'team'
      ? await apiClient.createTeamPrompt(activeWorkspace.teamId, input as CreatePromptInput)
      : await apiClient.createPrompt(input as CreatePromptInput);

    if (result.success) {
      const newPrompt = result.data.prompt;
      set((state) => ({ prompts: [...state.prompts, newPrompt] }));
      return newPrompt;
    }

    set({ error: result.error.message ?? result.error.error });
    return null;
  },

  updatePrompt: async (id, input) => {
    if (!apiClient) {
      set({ error: 'API client not configured' });
      return null;
    }

    const { prompts } = get();
    const originalPrompt = prompts.find((prompt) => prompt.id === id);

    if (!originalPrompt) {
      set({ error: 'Prompt not found' });
      return null;
    }

    const optimisticPrompt: CloudPrompt = {
      ...originalPrompt,
      title: input.title ?? originalPrompt.title,
      content: input.content ?? originalPrompt.content,
      folderId: input.folderId !== undefined ? input.folderId : originalPrompt.folderId,
      updatedAt: new Date().toISOString(),
    };

    set((state) => ({
      prompts: state.prompts.map((prompt) => (prompt.id === id ? optimisticPrompt : prompt)),
      error: null,
    }));

    const activeWorkspace = useTeamStore.getState().activeWorkspace;
    const result = activeWorkspace.type === 'team'
      ? await apiClient.updateTeamPrompt(activeWorkspace.teamId, id, input as UpdatePromptInput)
      : await apiClient.updatePrompt(id, input as UpdatePromptInput);

    if (result.success) {
      const updatedPrompt = result.data.prompt;
      set((state) => ({
        prompts: state.prompts.map((prompt) => (prompt.id === id ? updatedPrompt : prompt)),
      }));
      return updatedPrompt;
    }

    set((state) => ({
      prompts: state.prompts.map((prompt) => (prompt.id === id ? originalPrompt : prompt)),
      error: result.error.message ?? result.error.error,
    }));
    return null;
  },

  getPromptVersions: async (promptId) => {
    if (!apiClient) {
      set({ error: 'API client not configured' });
      return null;
    }

    set({ error: null });

    const activeWorkspace = useTeamStore.getState().activeWorkspace;
    const result = activeWorkspace.type === 'team'
      ? await apiClient.getTeamPromptVersions(activeWorkspace.teamId, promptId)
      : await apiClient.getVersions(promptId);

    if (result.success) {
      return result.data.versions;
    }

    set({ error: result.error.message ?? result.error.error });
    return null;
  },

  restorePromptVersion: async (promptId, versionId) => {
    if (!apiClient) {
      set({ error: 'API client not configured' });
      return null;
    }

    set({ error: null });

    const activeWorkspace = useTeamStore.getState().activeWorkspace;
    const result = activeWorkspace.type === 'team'
      ? await apiClient.restoreTeamPromptVersion(activeWorkspace.teamId, promptId, versionId)
      : await apiClient.restoreVersion(promptId, versionId);

    if (result.success) {
      const restoredPrompt = result.data.prompt;
      get().refreshPrompt(restoredPrompt);
      return restoredPrompt;
    }

    set({ error: result.error.message ?? result.error.error });
    return null;
  },

  refreshPrompt: (prompt) => {
    set((state) => ({
      prompts: state.prompts.map((candidate) =>
        candidate.id === prompt.id ? prompt : candidate,
      ),
    }));
  },

  deletePrompt: async (id) => {
    if (!apiClient) {
      set({ error: 'API client not configured' });
      return false;
    }

    const { prompts, selectedPromptId } = get();
    const originalPrompts = prompts;

    set((state) => ({
      prompts: state.prompts.filter((prompt) => prompt.id !== id),
      selectedPromptId: selectedPromptId === id ? null : selectedPromptId,
      error: null,
    }));

    const activeWorkspace = useTeamStore.getState().activeWorkspace;
    const result = activeWorkspace.type === 'team'
      ? await apiClient.deleteTeamPrompt(activeWorkspace.teamId, id)
      : await apiClient.deletePrompt(id);

    if (result.success) {
      return true;
    }

    set({
      prompts: originalPrompts,
      error: result.error.message ?? result.error.error,
    });
    return false;
  },

  createFolder: async (input) => {
    if (!apiClient) {
      set({ error: 'API client not configured' });
      return null;
    }

    set({ error: null });

    const activeWorkspace = useTeamStore.getState().activeWorkspace;
    const result = activeWorkspace.type === 'team'
      ? await apiClient.createTeamFolder(activeWorkspace.teamId, input as CreateFolderInput)
      : await apiClient.createFolder(input as CreateFolderInput);

    if (result.success) {
      const newFolder = result.data.folder;
      set((state) => ({ folders: [...state.folders, newFolder] }));
      return newFolder;
    }

    set({ error: result.error.message ?? result.error.error });
    return null;
  },

  renameFolder: async (id, input) => {
    if (!apiClient) {
      set({ error: 'API client not configured' });
      return null;
    }

    const { folders } = get();
    const originalFolder = folders.find((folder) => folder.id === id);

    if (!originalFolder) {
      set({ error: 'Folder not found' });
      return null;
    }

    const optimisticFolder: CloudFolder = {
      ...originalFolder,
      name: input.name,
    };

    set((state) => ({
      folders: state.folders.map((folder) =>
        folder.id === id ? optimisticFolder : folder,
      ),
      error: null,
    }));

    const activeWorkspace = useTeamStore.getState().activeWorkspace;
    const result = activeWorkspace.type === 'team'
      ? await apiClient.renameTeamFolder(activeWorkspace.teamId, id, input as UpdateFolderInput)
      : await apiClient.renameFolder(id, input as UpdateFolderInput);

    if (result.success) {
      const updatedFolder = result.data.folder;
      set((state) => ({
        folders: state.folders.map((folder) =>
          folder.id === id ? updatedFolder : folder,
        ),
      }));
      return updatedFolder;
    }

    set((state) => ({
      folders: state.folders.map((folder) =>
        folder.id === id ? originalFolder : folder,
      ),
      error: result.error.message ?? result.error.error,
    }));
    return null;
  },

  deleteFolder: async (id) => {
    if (!apiClient) {
      set({ error: 'API client not configured' });
      return false;
    }

    const { folders, prompts } = get();
    const originalFolders = folders;
    const originalPrompts = prompts;

    set((state) => ({
      folders: state.folders.filter((folder) => folder.id !== id),
      prompts: state.prompts.map((prompt) =>
        prompt.folderId === id ? { ...prompt, folderId: null } : prompt,
      ),
      error: null,
    }));

    const activeWorkspace = useTeamStore.getState().activeWorkspace;
    const result = activeWorkspace.type === 'team'
      ? await apiClient.deleteTeamFolder(activeWorkspace.teamId, id)
      : await apiClient.deleteFolder(id);

    if (result.success) {
      return true;
    }

    set({
      folders: originalFolders,
      prompts: originalPrompts,
      error: result.error.message ?? result.error.error,
    });
    return false;
  },

  setSelectedPromptId: (id) => {
    set({ selectedPromptId: id });
  },

  setSearchFilter: (filter) => {
    set({ searchFilter: filter });
  },

  clearError: () => {
    set({ error: null });
  },

  reset: () => {
    set(initialState);
  },
}));
